import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import Header from './Header';
import Sidebar from './Sidebar';
import ActionablesOpenAlert from '../actionables/ActionablesOpenAlert';
import OnboardingOrchestrator from '../onboarding/OnboardingOrchestrator';
import { useOnboarding } from '../onboarding/OnboardingContext';
import packageJson from '../../../package.json';

interface LayoutProps {
  children: React.ReactNode;
}

// Inner shell split out so it can consume the OnboardingContext provided by the
// orchestrator (the footer version chip opens Release History).
const LayoutShell: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { openReleaseHistory } = useOnboarding();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: 'background.paper' }}>
      {/* Header — pinned at top; flexShrink guard keeps it at its fixed height. */}
      <Box sx={{ flexShrink: 0 }}>
        <Header />
      </Box>
      
      {/* Mobile menu button removed per design request (header only shows) */}

      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Content */}
      <Box
        component="main"
        id="app-main-scroll"
        sx={{
          // Sole scroll container of the fixed app shell: the outer shell is height:100vh /
          // overflow:hidden with a fixed-height header and footer, so only this region scrolls.
          // `minHeight: 0` is required so this flex child can shrink below its content height —
          // otherwise it would grow to fit the content and the inner overflow would never engage.
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, md: 2.5 },
          bgcolor: 'background.default',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '200px',
            background: 'linear-gradient(135deg, rgba(220, 88, 41, 0.08) 0%, rgba(220, 88, 41, 0) 100%)',
            borderRadius: '0 0 24px 24px',
            zIndex: -1,
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {children}
        </Box>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.25,
          px: { xs: 2, sm: 3, md: 4 },
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          &copy; {new Date().getFullYear()} | Salty Accessories
        </Typography>
        <Box
          component="span"
          role="button"
          tabIndex={0}
          aria-label="View release history"
          onClick={openReleaseHistory}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openReleaseHistory();
            }
          }}
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            color: 'primary.main',
            bgcolor: 'rgba(220, 88, 41, 0.08)',
            border: '1px solid rgba(220, 88, 41, 0.2)',
            borderRadius: '999px',
            px: 1,
            py: 0.25,
            lineHeight: 1.4,
            cursor: 'pointer',
          }}
        >
          v{packageJson.version}
        </Box>
      </Box>

      {/* App-open nudge about open actionables — global (fires regardless of landing route),
          once per login session, non-blocking. Lives here (authed shell) so it never renders
          on the login page. */}
      <ActionablesOpenAlert />
    </Box>
  );
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  return (
    <OnboardingOrchestrator>
      <LayoutShell>{children}</LayoutShell>
    </OnboardingOrchestrator>
  );
};

export default Layout;